import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import styled from '@emotion/styled';

import { InlineNotification, Typography2 } from '@coursera/cds-core';
import { Box, Notification, color } from '@coursera/coursera-ui';

import { Clickable } from 'bundles/enterprise-ui/components/Clickable';
import { formatNumber } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-skills';

const LinkText = styled.div({
  color: color.primary,
  fontSize: '14px',
  lineHeight: '24px',
  textDecoration: 'underline',
});
LinkText.displayName = 'LinkText';

export const SkillSetsAddedNotification = ({ onDismiss, number }: { onDismiss: () => void; number: number }) => (
  <InlineNotification
    severity="success"
    onDismiss={onDismiss}
    title={
      (
        <FormattedMessage
          message={_t(
            // eslint-disable-next-line no-restricted-syntax
            '{number} {number, plural, =1 {SkillSet} other {SkillSets}} {number, plural, =1 {has} other {have}} been successfully added to your learning program'
          )}
          number={formatNumber(number)}
        />
      ) as unknown as string
    }
  >
    <Typography2 component="p">{_t('Learners in this program now have access to these SkillSets.')}</Typography2>
  </InlineNotification>
);

export const SkillSetsRemovedNotification = ({ onDismiss, name }: { onDismiss: () => void; name: string }) => (
  <Notification
    type="success"
    isDismissible
    onDismiss={onDismiss}
    message={
      <div>
        <strong>
          <Typography2 component="p">
            <FormattedMessage
              message={_t('The SkillSet "{name}" has been successfully removed from this learning program')}
              name={name}
            />
          </Typography2>
        </strong>
        <Typography2 component="p">
          {_t('Learners in this program will no longer have access to this SkillSet.')}
        </Typography2>
      </div>
    }
  />
);

export const ConflictingNamesNotification = (_props: {}) => (
  <Notification
    type="error"
    message={
      <Typography2 component="p">
        {_t(
          'Error: Some selected SkillSets have conflicting names with ones already in your organization, please review before proceeding'
        )}
      </Typography2>
    }
  />
);

export const NoSkillSetsNotification = ({ onBack }: { onBack: () => void }) => (
  <Notification
    type="error"
    message={
      <Box>
        <Typography2 component="p">
          {_t('Error: Please return to the previous step and select SkillSets to add.')}&nbsp;
          <Clickable onClick={onBack}>
            <LinkText>{_t('Go back to Add SkillSets')}</LinkText>
          </Clickable>
        </Typography2>
      </Box>
    }
  />
);

export const SuccessfullyResolvedErrorsNotification = (_props: {}) => (
  <Notification
    type="success"
    message={<Typography2 component="p">{_t('All issues regarding these SkillSets have been resolved.')}</Typography2>}
  />
);

export const SkillSetRenamedSuccessfullyNotification = ({
  oldName,
  newName,
  onDismiss,
}: {
  oldName?: string;
  newName?: string;
  onDismiss: () => void;
}) => (
  <Notification
    type="success"
    isDismissible
    data-e2e="skillset-renamed-notification"
    onDismiss={onDismiss}
    message={
      <Typography2 component="p">
        <FormattedMessage
          message={_t('The SkillSet “{oldName}” has been successfully renamed to “{newName}”.')}
          newName={newName}
          oldName={oldName}
        />
      </Typography2>
    }
  />
);

export const UpdatedSkillSetDescriptionSuccessfullyNotification = ({
  skillSetName,
  onDismiss,
}: {
  skillSetName: string;
  onDismiss: () => void;
}) => (
  <Notification
    type="success"
    isDismissible
    data-e2e="skillset-description-updated-notification"
    onDismiss={onDismiss}
    message={
      <Typography2 component="p">
        <FormattedMessage
          message={_t('The description for SkillSet "{skillSetName}" has been updated.')}
          skillSetName={skillSetName}
        />
      </Typography2>
    }
  />
);

export const DuplicateNameNotification = ({
  name,
  isProgramScope,
  onRename,
}: {
  name: string;
  isProgramScope?: boolean;
  onRename: () => void;
}) => (
  <Notification
    type="error"
    message={
      <Box>
        {isProgramScope && (
          <Typography2 component="p">
            <FormattedMessage
              message={_t(
                'The SkillSet ‘{name}’ is already in your program, you can create a new one based on it by renaming it,'
              )}
              name={name}
            />
            &nbsp;
            <Clickable onClick={onRename}>
              <LinkText>{_t('rename this SkillSet')}</LinkText>
            </Clickable>
          </Typography2>
        )}
        {!isProgramScope && (
          <Typography2 component="p">
            <FormattedMessage
              message={_t('Error: A SkillSet with the name ‘{name}’ already exist in your organization, please')}
              name={name}
            />
            &nbsp;
            <Clickable onClick={onRename}>
              <LinkText>{_t('rename.')}</LinkText>
            </Clickable>
          </Typography2>
        )}
      </Box>
    }
  />
);

export const UsefulTip = (_props: {}) => (
  <Notification
    message={
      <div>
        <Typography2 component="strong">{_t('Useful Tip')}</Typography2>
        <Typography2 component="p">
          {_t(
            `Adding SkillSets to your organization won’t make them immediately available to learners. You’ll need to add them to a learning program.`
          )}
        </Typography2>
      </div>
    }
  />
);
